import { AdCreativeDetails, AdCreativeScene } from '../../../queries';
import { AdBackground, AdElement, AdScene, createBackground, ShapeKind, TextAlign } from '../model/types';

const SHAPE_KINDS: ShapeKind[] = ['rect', 'ellipse', 'triangle', 'star', 'line'];
const ALIGNMENTS: TextAlign[] = ['left', 'center', 'right'];

const num = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const str = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const bool = (value: unknown, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);

/** Scena idzie do bazy jako czysty JSON, więc zapisujemy płytkie kopie. */
export const toSavedScene = (scene: AdScene): AdCreativeScene => ({
  background: { ...scene.background } as unknown as Record<string, unknown>,
  elements: scene.elements.map((element) => ({ ...element })) as unknown as Array<Record<string, unknown>>,
});

export const readBackground = (raw: Record<string, unknown> | undefined): AdBackground => {
  const base = createBackground();
  if (!raw) return base;
  const kind = str(raw.kind, base.kind);
  return {
    kind: kind === 'gradient' || kind === 'image' ? kind : 'solid',
    color: str(raw.color, base.color),
    gradientFrom: str(raw.gradientFrom, base.gradientFrom),
    gradientTo: str(raw.gradientTo, base.gradientTo),
    gradientAngle: num(raw.gradientAngle, base.gradientAngle),
    imageSrc: str(raw.imageSrc),
    imageStockId: num(raw.imageStockId, 0) || undefined,
    overlayColor: str(raw.overlayColor, base.overlayColor),
    overlayOpacity: num(raw.overlayOpacity, base.overlayOpacity),
  };
};

/**
 * Odtwarza element z dokumentu. Czytamy defensywnie, bo JSON w bazie mógł
 * powstać na starszej wersji modelu edytora, a jeden brakujący klucz nie może
 * wywracać całej kreacji.
 */
const readElement = (raw: Record<string, unknown>, index: number): AdElement | null => {
  const shared = {
    id: str(raw.id) || `restored-${index}`,
    name: str(raw.name, 'Element'),
    x: num(raw.x, 0),
    y: num(raw.y, 0),
    width: num(raw.width, 100),
    height: num(raw.height, 100),
    rotation: num(raw.rotation, 0),
    opacity: num(raw.opacity, 1),
    locked: bool(raw.locked),
    hidden: bool(raw.hidden),
    shadow: raw.shadow && typeof raw.shadow === 'object' ? {
      color: str((raw.shadow as Record<string, unknown>).color, '#000000'),
      blur: num((raw.shadow as Record<string, unknown>).blur, 0),
      offsetX: num((raw.shadow as Record<string, unknown>).offsetX, 0),
      offsetY: num((raw.shadow as Record<string, unknown>).offsetY, 0),
    } : undefined,
  };

  if (raw.type === 'text') {
    const align = str(raw.align, 'center') as TextAlign;
    return {
      ...shared,
      type: 'text',
      text: str(raw.text),
      fontFamily: str(raw.fontFamily, 'Montserrat'),
      fontSize: num(raw.fontSize, 48),
      fontWeight: num(raw.fontWeight, 700),
      fontStyle: raw.fontStyle === 'italic' ? 'italic' : 'normal',
      color: str(raw.color, '#ffffff'),
      align: ALIGNMENTS.includes(align) ? align : 'center',
      lineHeight: num(raw.lineHeight, 1.2),
      letterSpacing: num(raw.letterSpacing, 0),
      uppercase: bool(raw.uppercase),
      background: str(raw.background),
      backgroundRadius: num(raw.backgroundRadius, 0),
      padding: num(raw.padding, 0),
      stroke: str(raw.stroke),
      strokeWidth: num(raw.strokeWidth, 0),
      autoFit: bool(raw.autoFit),
    };
  }

  if (raw.type === 'shape') {
    const shape = str(raw.shape, 'rect') as ShapeKind;
    return {
      ...shared,
      type: 'shape',
      shape: SHAPE_KINDS.includes(shape) ? shape : 'rect',
      fill: str(raw.fill, '#c18344'),
      stroke: str(raw.stroke),
      strokeWidth: num(raw.strokeWidth, 0),
      radius: num(raw.radius, 0),
    };
  }

  if (raw.type === 'image') {
    return {
      ...shared,
      type: 'image',
      src: str(raw.src),
      radius: num(raw.radius, 0),
      fit: raw.fit === 'contain' ? 'contain' : 'cover',
      circle: bool(raw.circle),
      stockId: num(raw.stockId, 0) || undefined,
      focusX: num(raw.focusX, 0.5),
      focusY: num(raw.focusY, 0.5),
      blur: num(raw.blur, 0),
      brightness: num(raw.brightness, 0),
      contrast: num(raw.contrast, 0),
      grayscale: bool(raw.grayscale),
    };
  }

  return null;
};

export const readElements = (raw: AdCreativeScene['elements']): AdElement[] => raw
  .map((element, index) => readElement(element, index))
  .filter((element): element is AdElement => element !== null);

/** Zapisana kreacja wraca do edytora w swoim własnym formacie. */
export const fromSavedCreative = (details: AdCreativeDetails): AdScene => ({
  platformId: details.platformId,
  formatId: details.formatId,
  width: details.width,
  height: details.height,
  background: readBackground(details.scene?.background),
  elements: readElements(details.scene?.elements ?? []),
});

/** Hosty, z których pochodzą znakowane podglądy banku zdjęć. */
const PREVIEW_HOSTS = ['img.freepik.com', 'img.magnific.com'];

const isPreviewUrl = (url: unknown): boolean => typeof url === 'string' && PREVIEW_HOSTS.some((host) => url.includes(host));

/** Podgląd ze stocka leżący na planszy, razem z miejscem, w którym siedzi. */
export interface WatermarkedPreview {
  /** `null` = tło planszy; inaczej identyfikator elementu. */
  elementId: string | null;
  src: string;
  stockId?: number;
}

/**
 * Podglądy ze znakiem wodnym obecne na planszy.
 *
 * Sprawdzamy je przed eksportem: taka wersja jest robocza, nie wolno jej
 * opublikować, a przeglądarka i tak zablokuje zapis pliku, bo obraz wczytany
 * bez zgody CORS skaża płótno.
 */
export const collectWatermarkedPreviews = (scene: AdScene): WatermarkedPreview[] => {
  const found: WatermarkedPreview[] = [];

  if (isPreviewUrl(scene.background.imageSrc)) {
    found.push({
      elementId: null,
      src: scene.background.imageSrc,
      stockId: scene.background.imageStockId,
    });
  }

  scene.elements.forEach((element) => {
    if (element.type === 'image' && isPreviewUrl(element.src)) {
      found.push({ elementId: element.id, src: element.src, stockId: element.stockId });
    }
  });

  return found;
};

export const hasWatermarkedPreview = (scene: AdScene): boolean => collectWatermarkedPreviews(scene).length > 0;

const SCALED_KEYS = [
  'fontSize', 'letterSpacing', 'padding', 'backgroundRadius', 'radius', 'strokeWidth',
] as const;

/**
 * Przenosi elementy na planszę o innych wymiarach.
 *
 * Skalujemy jednym współczynnikiem (mniejszym z dwóch) i dosuwamy zawartość do
 * środka: skalowanie osobno w poziomie i w pionie rozciągałoby litery i koła.
 * Dzięki temu szablon zapisany jako kwadrat 1080x1080 da się użyć na relacji
 * 9:16 bez poprawiania każdego elementu z osobna.
 */
export const scaleElements = (
  elements: AdElement[],
  from: { width: number; height: number },
  to: { width: number; height: number },
): AdElement[] => {
  if (from.width === to.width && from.height === to.height) return elements;
  if (!from.width || !from.height) return elements;

  const scale = Math.min(to.width / from.width, to.height / from.height);
  const offsetX = (to.width - from.width * scale) / 2;
  const offsetY = (to.height - from.height * scale) / 2;

  return elements.map((element) => {
    const scaled: AdElement = {
      ...element,
      x: Math.round(element.x * scale + offsetX),
      y: Math.round(element.y * scale + offsetY),
      width: Math.max(1, Math.round(element.width * scale)),
      height: Math.max(1, Math.round(element.height * scale)),
      shadow: element.shadow ? {
        ...element.shadow,
        blur: element.shadow.blur * scale,
        offsetX: element.shadow.offsetX * scale,
        offsetY: element.shadow.offsetY * scale,
      } : undefined,
    };

    SCALED_KEYS.forEach((key) => {
      const value = (element as unknown as Record<string, unknown>)[key];
      if (typeof value === 'number') {
        (scaled as unknown as Record<string, unknown>)[key] = Math.round(value * scale * 100) / 100;
      }
    });

    return scaled;
  });
};

/** Szablon z bazy nałożony na bieżącą planszę: tło bez zmian, elementy przeskalowane. */
export const applySavedTemplate = (
  details: AdCreativeDetails,
  target: { width: number; height: number },
): { background: AdBackground; elements: AdElement[] } => ({
  background: readBackground(details.scene?.background),
  elements: scaleElements(
    readElements(details.scene?.elements ?? []),
    { width: details.width || target.width, height: details.height || target.height },
    target,
  ),
});
